import 'react-datepicker/dist/react-datepicker.css';
import * as Yup from 'yup';
import { DatePickerField } from '../../../components/common/admin/DatePickerField';
import { ErrorMessage, Formik } from 'formik';
import { Form, Modal } from 'react-bootstrap';
import { Game, useEditGameMutation, useGetAdminGameQuery, useResetGameMutation } from '../../../gql-types';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { ZDAR_ESCAPE_GAME_NAME, ZDAR_KIDS_GAME_NAME } from '../../../configuration/gamesConfiguration';
import { toast } from 'react-toastify';
import { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../../../components/common/buttons/Button';
import Loading from '../../../components/common/Loading';

export default function EditGame() {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { token } = useContext(ProfileContext);

    const { data, loading } = useGetAdminGameQuery({
        variables: { _id: id, jwt: token || '' },
        fetchPolicy: 'network-only',
    });
    const selectedGame = data?.adminGame ?? null;

    const [editGame] = useEditGameMutation();
    const [resetGame] = useResetGameMutation();
    const [modalOpen, setModalOpen] = useState(false);
    const [gameToReset, setGameToReset] = useState<Game | undefined | null>(undefined);

    const onModalClose = () => {
        setModalOpen(false);
        setGameToReset(undefined);
    };

    const goBack = () => {
        history.push('/admin');
    };

    const onResetClick = () => {
        setModalOpen(true);
        setGameToReset(selectedGame);
    };

    if (loading) {
        return <Loading color="#23ab70" />;
    }

    if (!selectedGame) {
        return null;
    }

    const schema = Yup.object({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        email: Yup.string().email().required(),
        code: Yup.string()
            .required()
            .matches(/^[0-9a-zA-Z]+$/, 'Kód může obsahovat pouze písmena a číslice')
            .length(10, 'Kód musí mít přesně 10 znaků'),
        gameType: Yup.string().required('Vyberte typ hry'),
        validFrom: Yup.date().required(),
        validTo: Yup.date().required(),
        used: Yup.string().required(),
        snow: Yup.string().required(),
        started: Yup.string(),
        ended: Yup.string(),
        lastStep: Yup.number().required(),
        penalties: Yup.number().required(),
        teamName: Yup.string(),
        finalTime: Yup.string(),
        token: Yup.string(),
    });

    const convertSecondsToDate = (seconds: string | null) => {
        if (seconds === null) {
            return new Date();
        }
        const t = new Date(1970, 0, 1);
        t.setTime(parseInt(seconds) * 1000);
        return t;
    };

    const handleReset = () => {
        if (gameToReset) {
            const gameObject = {
                variables: {
                    ...selectedGame,
                    used: false,
                    started: null,
                    ended: null,
                    lastStep: 0,
                    penalties: 0,
                    teamName: null,
                    finalTime: null,
                    snow: null,
                    token: null,
                    jwt: token,
                    emailExerciseSent: false,
                },
            };
            // @ts-expect-error
            resetGame(gameObject)
                .then(() => {
                    goBack();
                    toast('Hra úspěšně resetována.', { type: 'success' });
                })
                .catch((err) => {
                    toast('Chyba - hra neuložena.', { type: 'error' });
                    console.log(err);
                });
        }
    };

    const getStatsName = (col: number) => {
        if (col === 1) {
            return <div className="stats-cell">Stanovište</div>;
        }
        if (col === 2) {
            return <div className="stats-cell">Audio</div>;
        }
        if (col === 3) {
            return <div className="stats-cell">Nápovedy</div>;
        }
        if (col === 4) {
            return <div className="stats-cell">Chyby</div>;
        }
        if (col === 5) {
            return <div className="stats-cell">Čas</div>;
        }
        if (col === 6) {
            return <div className="stats-cell">Mapa</div>;
        }
        if (col === 7) {
            return <div className="stats-cell">Report</div>;
        }
        if (col === 8) {
            return <div className="stats-cell">Kódy</div>;
        }
    };

    return (
        <div className="admin-add-mode-wrapper">
            <Button className="primary-button" onClick={goBack}>
                Zpět
            </Button>
            <Modal show={modalOpen} onHide={onModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Reset</Modal.Title>
                </Modal.Header>
                <Modal.Body>Skutečně chcete resetovat hru s kódem {gameToReset?.code} ?</Modal.Body>
                <Modal.Footer>
                    <Button className="secondary-button" onClick={handleReset}>
                        Reset
                    </Button>
                    <Button className="secondary-button" onClick={onModalClose}>
                        Storno
                    </Button>
                </Modal.Footer>
            </Modal>
            <Formik
                validationSchema={schema}
                onSubmit={(values) => {
                    const from = values.validFrom;
                    const to = values.validTo;
                    from.setHours(0);
                    from.setMinutes(0);
                    from.setSeconds(0);
                    from.setMilliseconds(0);
                    to.setHours(23);
                    to.setMinutes(59);
                    to.setSeconds(59);
                    to.setMilliseconds(999);
                    const gameObject = {
                        variables: {
                            ...values,
                            stats: selectedGame?.stats,
                            _id: selectedGame['_id'],
                            validFrom: Math.floor(from.getTime() / 1000).toString(),
                            validTo: Math.floor(to.getTime() / 1000).toString(),
                            started: values.started === '' ? null : values.started,
                            ended: values.ended === '' ? null : values.ended,
                            teamName: values.teamName === '' ? null : values.teamName,
                            finalTime: values.finalTime === '' ? null : values.finalTime,
                            used: values.used === 'true',
                            snow: values.snow === 'true' ? true : values.snow === 'false' ? false : null,
                            lastStep: parseInt(values.lastStep.toString()),
                            penalties: parseInt(values.penalties.toString()),
                            token: values.token === '' ? null : values.token,
                            jwt: token,
                            emailExerciseSent: values.emailExerciseSent,
                        },
                    };
                    // @ts-expect-error
                    editGame(gameObject)
                        .then(() => {
                            goBack();
                            toast('Hra úspěšně upravena.', { type: 'success' });
                        })
                        .catch((err) => {
                            toast('Chyba - hra neuložena.', { type: 'error' });
                            console.log(err);
                        });
                }}
                initialValues={{
                    firstName: selectedGame.firstName ?? '',
                    lastName: selectedGame.lastName ?? '',
                    email: selectedGame.email ?? '',
                    code: selectedGame.code ?? '',
                    gameType: selectedGame.gameType ?? '',
                    snow: selectedGame.snow ? 'true' : selectedGame.snow === false ? 'false' : 'null',
                    validFrom: convertSecondsToDate(selectedGame.validFrom || null),
                    validTo: convertSecondsToDate(selectedGame.validTo || null),
                    used: selectedGame.used ? 'true' : 'false',
                    started: selectedGame.started ?? '',
                    ended: selectedGame.ended ?? '',
                    lastStep: selectedGame.lastStep ?? '',
                    penalties: selectedGame.penalties ?? '',
                    teamName: selectedGame.teamName ?? '',
                    finalTime: selectedGame.finalTime ?? '',
                    token: selectedGame.token ?? '',
                    emailExerciseSent: selectedGame.emailExerciseSent ?? null,
                }}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
            >
                {({ handleSubmit, handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Button className="secondary-button" onClick={onResetClick}>
                            Reset
                        </Button>
                        <Form.Row>
                            <Form.Group controlId="email">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.email && !errors.email}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'email'}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="firstName">
                                <Form.Label>Jméno</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.firstName && !errors.firstName}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'firstName'}
                                />
                            </Form.Group>
                            <Form.Group controlId="lastName">
                                <Form.Label>Příjmení</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.lastName && !errors.lastName}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'lastName'}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="code">
                                <Form.Label>Kód</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="code"
                                    value={values.code}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.code && !errors.code}
                                    maxLength={10}
                                />
                                <ErrorMessage render={(msg) => <div className="form-error">{msg}</div>} name={'code'} />
                            </Form.Group>
                            <Form.Group controlId="gameType">
                                <Form.Label>Typ hry</Form.Label>
                                <Form.Control
                                    as="select"
                                    type="select"
                                    name="gameType"
                                    value={values.gameType}
                                    onChange={handleChange}
                                    isValid={touched.gameType && !errors.gameType}
                                >
                                    <option>-</option>
                                    <option value={ZDAR_ESCAPE_GAME_NAME}>Žďárska únikovka</option>
                                    <option value={ZDAR_KIDS_GAME_NAME}>Dětská únikovka</option>
                                </Form.Control>
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'gameType'}
                                />
                            </Form.Group>
                            <Form.Group controlId="snow">
                                <Form.Label>Sníh</Form.Label>
                                <Form.Control
                                    as="select"
                                    type="select"
                                    name="snow"
                                    value={values.snow}
                                    onChange={handleChange}
                                    isValid={touched.snow && !errors.snow}
                                >
                                    <option value={'null'}>Nezadáno</option>
                                    <option value={'true'}>Ano</option>
                                    <option value={'false'}>Ne</option>
                                </Form.Control>
                                <ErrorMessage render={(msg) => <div className="form-error">{msg}</div>} name={'snow'} />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="validFrom">
                                <Form.Label>Platnost od</Form.Label>
                                <div className="datepicker-wrapper">
                                    <DatePickerField
                                        name="validFrom"
                                        value={values.validFrom}
                                        onChange={handleChange}
                                        isValid={touched.validFrom && !errors.validFrom}
                                    />
                                </div>
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'validFrom'}
                                />
                            </Form.Group>
                            <Form.Group controlId="validTo">
                                <Form.Label>Platnost do</Form.Label>
                                <div className="datepicker-wrapper">
                                    <DatePickerField
                                        name="validTo"
                                        value={values.validTo}
                                        onChange={handleChange}
                                        isValid={touched.validTo && !errors.validTo}
                                    />
                                </div>
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'validTo'}
                                />
                            </Form.Group>
                            <Form.Group controlId="validToButton">
                                <Button
                                    className="info-button"
                                    onClick={() => {
                                        const newDate = new Date(values.validFrom);
                                        newDate.setMonth(values.validFrom.getMonth() + 6);
                                        setFieldValue('validTo', newDate);
                                    }}
                                >
                                    {`Nastavit "Platnost do" za 6 měsíců`}
                                </Button>
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="started">
                                <Form.Label>Začátek hry (s) - timestamp</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="started"
                                    value={values.started}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.started && !errors.started}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'started'}
                                />
                            </Form.Group>
                            <Form.Group controlId="ended">
                                <Form.Label>Konec hry (s) - timestamp</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="ended"
                                    value={values.ended}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.ended && !errors.ended}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'ended'}
                                />
                            </Form.Group>
                            <Form.Group controlId="finalTime">
                                <Form.Label>Finální čas</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="finalTime"
                                    value={values.finalTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.finalTime && !errors.finalTime}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'finalTime'}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="teamName">
                                <Form.Label>Jméno týmu</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="teamName"
                                    value={values.teamName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.teamName && !errors.teamName}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'teamName'}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="penalties">
                                <Form.Label>Penalizace</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="penalties"
                                    value={values.penalties}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.penalties && !errors.penalties}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'penalties'}
                                />
                            </Form.Group>
                            <Form.Group controlId="lastStep">
                                <Form.Label>Poslední krok</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="lastStep"
                                    value={values.lastStep}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.lastStep && !errors.lastStep}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'lastStep'}
                                />
                            </Form.Group>
                            <Form.Group controlId="token">
                                <Form.Label>JWT token</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="token"
                                    value={values.token}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.token && !errors.token}
                                />
                                <ErrorMessage
                                    render={(msg) => <div className="form-error">{msg}</div>}
                                    name={'token'}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group controlId="used">
                                <Form.Label>Hra ukončena</Form.Label>
                                <Form.Control
                                    as="select"
                                    type="select"
                                    name="used"
                                    value={values.used.toString()}
                                    onChange={handleChange}
                                    isValid={touched.used && !errors.used}
                                >
                                    <option>-</option>
                                    <option value="true">true</option>
                                    <option value="false">false</option>
                                </Form.Control>
                                <ErrorMessage render={(msg) => <div className="form-error">{msg}</div>} name={'used'} />
                            </Form.Group>
                        </Form.Row>
                        {values.emailExerciseSent !== null && (
                            <Form.Row>
                                <Form.Group controlId="emailExerciseSent">
                                    <Form.Label>Pracovní list poslán</Form.Label>
                                    <Form.Control
                                        as="select"
                                        type="select"
                                        name="emailExerciseSent"
                                        value={values.emailExerciseSent.toString()}
                                        onChange={handleChange}
                                        isValid={touched.emailExerciseSent && !errors.emailExerciseSent}
                                    >
                                        <option>-</option>
                                        <option value="true">true</option>
                                        <option value="false">false</option>
                                    </Form.Control>
                                    <ErrorMessage
                                        render={(msg) => <div className="form-error">{msg}</div>}
                                        name={'emailExerciseSent'}
                                    />
                                </Form.Group>
                            </Form.Row>
                        )}
                        <Button className="secondary-button secondary-button__margin" onClick={handleSubmit}>
                            Uložit
                        </Button>
                    </Form>
                )}
            </Formik>
            <div className="admin-stats-wrapper">
                <div>Statistiky</div>
                {selectedGame.stats !== null &&
                    selectedGame.stats &&
                    Object.entries(selectedGame.stats).map(([k, v]) => {
                        if (k !== '__typename') {
                            return (
                                <div className="stats-wrapper" key={k.substr(1, 10)}>
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(1)}</div>
                                        <div className="stats-cell">{k.substr(1, 10)}</div>
                                    </div>
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(2)}</div>
                                        {/*// @ts-expect-error*/}
                                        <div className="stats-cell">{v?.audioPlayed ? 'Ano' : 'Ne'}</div>
                                    </div>
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(3)}</div>
                                        {/*// @ts-expect-error*/}
                                        <div className="stats-cell">{v?.hintsTaken}</div>
                                    </div>
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(4)}</div>
                                        {/*// @ts-expect-error*/}
                                        <div className="stats-cell">{v?.mistakes}</div>
                                    </div>
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(5)}</div>
                                        {/*// @ts-expect-error*/}
                                        <div className="stats-cell">{v?.timeSpent}</div>
                                    </div>
                                    {selectedGame.gameType === ZDAR_KIDS_GAME_NAME && (
                                        <>
                                            <div className="stats-row">
                                                <div className="stats-cell">{getStatsName(6)}</div>
                                                {/*// @ts-expect-error*/}
                                                <div className="stats-cell">{v?.reportClicked ? 'Ano' : 'Ne'}</div>
                                            </div>
                                            <div className="stats-row">
                                                <div className="stats-cell">{getStatsName(7)}</div>
                                                {/*// @ts-expect-error*/}
                                                <div className="stats-cell">{v?.mapClicked ? 'Ano' : 'Ne'}</div>
                                            </div>
                                        </>
                                    )}
                                    <div className="stats-row">
                                        <div className="stats-cell">{getStatsName(8)}</div>
                                        {/*// @ts-expect-error*/}
                                        <div className="stats-cell">{v?.codes.join(', ')}</div>
                                    </div>
                                </div>
                            );
                        }
                    })}
            </div>
            <div className="bottom-margin" />
        </div>
    );
}
